import { useEffect } from 'react';
import type { RefObject } from 'react';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

function getOffsetFrom(viewport: VisualViewport) {
  return (
    document.body.getBoundingClientRect().height -
    viewport.offsetTop -
    viewport.height
  );
}

type Args = {
  ref: RefObject<HTMLElement>;
};

/**
 * `useDeviceFixedBottom` - observes changes in the 'visualViewport', ensures
 * the bottom of the (ref) element remains fixed to the bottom of the screen.
 * Note: fix applied to iOS browsers only (not the Capacitor WebView), as the
 * issue has only been observed there.
 *
 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/VisualViewport
 */
export function useDeviceFixedBottom({ ref }: Args) {
  const { os } = useUserAgent();

  useEffect(() => {
    const shouldManageViewport =
      'visualViewport' in window && os === 'iOS' && RUNTIME_ENV !== 'native';

    if (!shouldManageViewport) {
      return;
    }

    function handleViewportChange() {
      const element = ref?.current;
      if (element && visualViewport !== null) {
        element.style.bottom = `${getOffsetFrom(visualViewport)}px`;
      }
    }

    visualViewport?.addEventListener('resize', handleViewportChange);

    return () => {
      visualViewport?.removeEventListener('resize', handleViewportChange);
    };
  }, [os, ref]);
}
